import React, { useState, useEffect } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useAccount } from "utils/AccountInfoContext";
import Header from "../Header";
import Button from "../Button";
import VerifyEmailModal from "../VerifyEmailModal";
import styles from "./ReviewEmail.module.css";
import { InputItem } from "../InputItem/InputItem";
import { useTranslation } from "react-i18next";
import {
  useEmail,
  useValidateAndVerifyEmail,
  useVerifyEmail,
} from "./useEmail";

export function ReviewEmail() {
  const {t} = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const { activeUser, subEntityData, cachedIBOInfo } = useAccount();
  // const contactId = activeUser?.contactId;
  const pinLevelMapping = {
    2: "Platinum Settings",
    3: "Emerald Settings",
    4: "Diamond Settings",
  };

  const pinLevel = cachedIBOInfo?.PinLevel || 0;
  const headerText = pinLevelMapping[pinLevel];
  const contactId = "0";

  const selectedEntity = subEntityData.find(
    (item) => item.entityId === parseInt(id)
  );

  const { mutate: confirmEmail, isSuccess } = useEmail();
  const { validateUpdateEmail, verifyEmail } = useValidateAndVerifyEmail();
  const { verifySubEntityEmail } = useVerifyEmail(id);

  const [email, setEmail] = useState("");

  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [editing, setEditing] = useState(false);

  const [emailStatus, setEmailStatus] = useState(null);
  const [bankRequired, setBankRequired] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (selectedEntity) {
      setEmail(selectedEntity.email);
      setEmailStatus(selectedEntity.emailStatus);
      setBankRequired(selectedEntity.bankRequired);
    }
  }, [selectedEntity]);

  const handleSkipClick = () => {
    const nextPath = bankRequired
      ? `/review-bank-info/${selectedEntity.entityId}`
      : `/review-w9/${selectedEntity.entityId}`;
    history.push({
      pathname: nextPath,
    });
  };

  const handleHeaderClick = () => {
    history.push("/platinum-settings");
  };

  const validateEmail = (email) => {
    if (typeof email !== "string") {
      setErrorMessage(t("Invalid email format"));
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setErrorMessage(t("Please enter an email address"));
      return false;
    } else if (!emailRegex.test(email)) {
      setErrorMessage(t("Please enter a valid email address"));
      return false;
    } else {
      setErrorMessage("");
      return true;
    }
  };

  //Triggers email is correct button
  const handleEmailConfirmClick = (emailToConfirm) => {
    if (!validateEmail(emailToConfirm || email)) {
      return;
    }
    confirmEmail(
      { entityId: selectedEntity.entityId, email: emailToConfirm || email },
      {
        onSuccess: () => {
          const nextPath = bankRequired
            ? `/review-bank-info/${selectedEntity.entityId}`
            : `/review-w9/${selectedEntity.entityId}`;
          history.push({
            pathname: nextPath,
          });
        },
        onError: (error) => {
          console.error(t("Error confirming email"), error);
        },
      }
    );
  };

  //Triggers to validate email button
  const handleValidateEmail = async () => {
    if (!validateEmail(email)) {
      return false;
    }

    try {
      await validateUpdateEmail.mutateAsync({ contactId: contactId, email });
    } catch (error) {
      console.error(t("Failed to update email"), error);
    }

    setEmail(email);
    setEditing(false);
    setShowVerifyModal(true);
    setErrorMessage("");
  };

  //Triggers to verify button
  const handleVerifyEmail = async (email, code) => {
    try {
      if (typeof email !== "string" || !email.trim()) {
        throw new Error(t("Invalid email format"));
      }

      const requestBody = { contactId, email, code };

      // First call to verifyEmail
      const verifyEmailResponse = await verifyEmail.mutateAsync(requestBody);

      const isVerificationSuccess =
        verifyEmailResponse === true ||
        verifyEmailResponse === "True" ||
        verifyEmailResponse === "success";

      //If response is True call proceed
      if (isVerificationSuccess) {
        await verifySubEntityEmail.mutateAsync({
          entityId: selectedEntity.entityId,
          email,
        });

        const nextPath = bankRequired
          ? `/review-bank-info/${selectedEntity.entityId}`
          : `/review-w9/${selectedEntity.entityId}`;
        history.push({ pathname: nextPath });
        return true;
      }

      return false;
    } catch (error) {
      console.error(t("Failed to verify email"), error);
      return false;
    }
  };

  const handleUpdateEmailClick = () => {
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setErrorMessage("");
  };

  const handleCloseModal = () => {
    setShowVerifyModal(false);
    setErrorMessage("");
  };

  const handleBack = () => {
    setShowVerifyModal(false);
  };

  const renderButtons = () => {
    if (editing) {
      return (
        <div className={styles.buttonsContainer}>
          <Button
            onClick={handleValidateEmail}
            buttonText={t("Validate Email Address")}
            disabled={!email}
          />
          <Button onClick={handleCancelEdit} buttonText={t("Cancel")} />
        </div>
      );
    }

    switch (emailStatus) {
      case 3:
        return (
          <div className={styles.buttonsContainer}>
            <Button
              onClick={handleValidateEmail}
              buttonText={t("Validate This Email")}
              // disabled={!newEmail}
              disabled={!email}
              className={styles.ctrInfoButton}
            />
          </div>
        );
      case 2:
        return (
          <div className={styles.buttonsContainer}>
            <Button
              onClick={handleValidateEmail}
              buttonText={t("Validate This Email")}
            />
            <Button
              onClick={handleUpdateEmailClick}
              buttonText={t("Update Email")}
            />
          </div>
        );
      case 1:
      case 6:
      case 7:
        return (
          <div className={styles.buttonsContainer}>
            <Button
              onClick={() => handleEmailConfirmClick()}
              buttonText={t("Email is Correct")}
              className={styles.ctrInfoButton}
            />
            <Button
              onClick={handleUpdateEmailClick}
              buttonText={t("Update Email")}
              className={styles.updateButton}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <Header header={t(headerText)} onClick={handleHeaderClick} />
      <div className={styles.flexContainer}>
        <span className={styles.header}>{t("Review Email Address")}</span>
        <Button onClick={handleSkipClick} buttonText={t("Skip")} className={styles.skipButton}/>
      </div>

      <span className={styles.addressLabel}>
        {selectedEntity?.name || "ABC Company"}
      </span>
      <div className={styles.addressBody}>
        {editing || emailStatus === 3 ? (
          <div className={styles.inputContainer}>
            <label htmlFor="email" className={styles.emailInput}>
              {t("Email")}
            </label>
            <div className={styles.inputButtonWidth}>
              <InputItem
                type="text"
                id="email"
                label={""}
                name="email"
                // value={newEmail}
                // setValue={(e) => setNewEmail(e)}
                value={email}
                // setValue={(e) => setEmail(e.target.value)}
                setValue={setEmail}
                placeholder={""}
              />
            </div>
            {errorMessage && (
              <div className={styles.errorMessage}>{errorMessage}</div>
            )}
          </div>
        ) : (
          <span>{email}</span>
        )}
      </div>

      {renderButtons()}

      {showVerifyModal && (
        <div className={styles.modalBackdrop}>
          <VerifyEmailModal
            // email={tempEmail}
            email={email}
            onClose={handleCloseModal}
            onVerify={handleVerifyEmail}
            onBack={handleBack}
            onResendEmail={handleValidateEmail}
          />
        </div>
      )}
    </div>
  );
}

export default ReviewEmail;
